import { zodResolver } from '@hookform/resolvers/zod';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IMaskInput } from 'react-imask';
import { useParams } from 'react-router-dom';
import { z } from 'zod';
import { UpdateUserRequest, UserRole } from '../apiClients/workspace/types';
import {
  Button,
  Card,
  CardContent,
  Input,
  PageHeader,
  PageLayout,
  UserSkillLevelCard,
} from '../components/shared';
import { getRoleDisplayKey } from '../lib/userUtils';
import { useUserStore } from '../stores';

type ProfileFormData = {
  name: string;
  // email?: string;
  phone?: string;
  birthday?: string;
};

const Profile: React.FC = observer(() => {
  const params = useParams();
  const { workspaceSlug, userId } = params;
  const userStore = useUserStore();
  const { t } = useTranslation();
  const [isFormInitialized, setIsFormInitialized] = useState(false);

  const isCurrentUser = !userId || userId === userStore.userId;
  const targetUser = isCurrentUser
    ? userStore.user
    : userStore.users.get(userId);
  // const isTargetUserAdmin = targetUser?.roles?.includes(UserRole.Admin);

  const isAdmin = userStore.user?.roles?.includes(UserRole.Admin);
  const canEdit = isCurrentUser || isAdmin;

  // Create schema with translations
  // Memoize the schema to avoid unnecessary re-creation on each render
  const profileSchema = React.useMemo(
    () =>
      z.object({
        name: z.string().min(1, t('validation.nameRequired')),
        // email: z
        //   .email(t('validation.invalidEmail'))
        //   .or(z.string().max(0))
        //   .optional(),
        phone: z
          .string()
          .optional()
          .refine(
            (value) => value?.length === 11 && value.startsWith('7'),
            t('validation.invalidPhone')
          )
          .or(z.string().max(0)),
        birthday: z.string().optional(),
      }),
    [t]
  );

  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: '',
      // email: '',
      phone: '',
      birthday: '',
    },
  });

  // Initialize form data when user data loads (only once)
  useEffect(() => {
    if (targetUser && !userStore.isLoading && !isFormInitialized) {
      const initialValues = {
        name: targetUser.name || '',
        // email: targetUser.email || '',
        phone: targetUser.phone || '',
        birthday: targetUser.birthday?.split('T')[0] || '',
      };

      form.reset(initialValues);
      setIsFormInitialized(true);
    }
  }, [targetUser, userStore.isLoading, isFormInitialized, form]);

  // Handle form submission
  const onSubmit = async (data: ProfileFormData) => {
    if (!targetUser?.id || !canEdit) return;

    const updateData: UpdateUserRequest = Object.fromEntries(
      Object.entries(data).map(([key, value]) => [key, value || null])
    );

    try {
      await userStore.updateById(targetUser.id, updateData);
      // Reset form state after successful update to remove dirty flag
      form.reset(data);
    } catch (error) {
      console.error('Failed to update profile:', error);
    }
  };

  // Don't render if workspace slug is not available yet
  if (!workspaceSlug) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 pb-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('workspace.loading')}</p>
        </div>
      </div>
    );
  }

  // Loading state - only show loading screen if we don't have the user data yet
  if (userStore.isLoading && !targetUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 pb-20">
        <PageHeader
          title={t('profile.title')}
          subtitle={t('profile.loadingSubtitle')}
          icon="👤"
        />
        <div className="container mx-auto px-4 -mt-8">
          <Card className="max-w-2xl mx-auto shadow-xl">
            <CardContent className="p-8">
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span className="text-gray-600">{t('common.loading')}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // User not found state
  if (!targetUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 pb-20">
        <PageHeader
          title={t('profile.title')}
          subtitle="User not found"
          icon="❌"
        />
        <div className="container mx-auto px-4 -mt-8">
          <Card className="max-w-2xl mx-auto shadow-xl">
            <CardContent className="p-8 text-center">
              <p className="text-gray-600">
                The user you&apos;re looking for doesn&apos;t exist.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <PageLayout
      title={targetUser?.name || 'User'}
      subtitle={
        targetUser?.tgUsername ? `@${targetUser.tgUsername}` : undefined
      }
      userAvatar={{
        src: targetUser?.photoUrl,
        alt: targetUser?.name || 'User',
        birthday: targetUser?.birthday,
      }}
      copyableSubtitle={!!targetUser?.tgUsername}
    >
      <Card className="max-w-2xl mx-auto shadow-xl">
        <CardContent className="p-8">
          {/* Back button */}
          <div className="mb-6">
            <Button
              variant="outline"
              onClick={() => window.history.back()}
              className="flex items-center gap-2"
            >
              <span>←</span>
              {t('common.back')}
            </Button>
          </div>
          {/* User Info Summary - Above Form */}

          <div className="mb-6 space-y-4">
            {/* Skill Level */}
            <>
              <UserSkillLevelCard
                user={targetUser}
                isCurrentUser={isCurrentUser}
                isAdmin={isAdmin ?? false}
              />

              {/* Statistics (WIP) */}
              <div className="grid grid-cols-2 gap-3">
                <div
                  className="p-3 bg-green-50 rounded-lg border border-green-200 pointer-events-none opacity-50 select-none"
                  aria-disabled="true"
                >
                  <p className="text-lg font-bold text-green-800">{'?'}</p>
                  <p className="text-xs text-green-600">
                    {t('team.totalTrainings')}
                  </p>
                </div>
                <div
                  className="p-3 bg-blue-50 rounded-lg border border-blue-200 pointer-events-none opacity-50 select-none"
                  aria-disabled="true"
                >
                  <p className="text-lg font-bold text-blue-800">{'?'}</p>
                  <p className="text-xs text-blue-600">{t('team.thisMonth')}</p>
                </div>
                <div className="col-span-2 -mt-2 text-center">
                  <p className="text-[10px] italic text-gray-500">
                    {t('common.inDevelopment')}
                  </p>
                </div>
              </div>
            </>

            {/* User Roles */}
            {targetUser?.roles && targetUser.roles.length > 0 && (
              <div className="flex items-center flex-start justify-center">
                <div className="flex flex-wrap gap-2">
                  {targetUser.roles.map((role, index) => (
                    <span
                      key={index}
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        role === 1
                          ? 'bg-red-100 text-red-800 border border-red-200'
                          : 'bg-gray-100 text-gray-800 border border-gray-200'
                      }`}
                    >
                      {role === 1 ? '👑' : '👤'} {t(getRoleDisplayKey([role]))}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* TODO Refactor this form to use separate readonly and form views */}
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Personal Information */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <span className="text-2xl mr-3">ℹ️</span>
                {t('profile.basicInformation')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.name')}
                  </label>
                  {canEdit ? (
                    <>
                      <Input
                        type="text"
                        placeholder={t('profile.name')}
                        {...form.register('name')}
                      />
                      {form.formState.errors.name && (
                        <p className="text-red-500 text-sm mt-1">
                          {form.formState.errors.name.message}
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="flex h-10 w-full rounded-md border border-input bg-gray-50 px-3 py-2 text-sm text-gray-700">
                      {targetUser?.name || '-'}
                    </div>
                  )}
                </div>

                {/* <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.email')}
                  </label>
                  <Input
                    type="email"
                    placeholder={t('profile.email')}
                    {...form.register('email')}
                    readOnly={!canEdit}
                    className={`${!canEdit ? 'bg-gray-100 text-gray-600' : ''}`}
                  />
                  {form.formState.errors.email && (
                    <p className="text-red-500 text-sm mt-1">
                      {form.formState.errors.email.message}
                    </p>
                  )}
                </div> */}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.phone')}
                  </label>
                  {canEdit ? (
                    <>
                      <Controller
                        name="phone"
                        control={form.control}
                        render={({ field: { onChange, value } }) => (
                          <IMaskInput
                            mask="+7 (000) 000-00-00"
                            placeholder="+7 (___) ___-__-__"
                            value={value}
                            onAccept={(val) => {
                              if (val !== value) {
                                onChange(val.replace(/\D/g, ''));
                              }
                            }}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          />
                        )}
                      />
                      {form.formState.errors.phone && (
                        <p className="text-red-500 text-sm mt-1">
                          {form.formState.errors.phone.message}
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="flex h-10 w-full rounded-md border border-input bg-gray-50 px-3 py-2 text-sm text-gray-700">
                      {targetUser?.phone
                        ? `+7 (${targetUser.phone.slice(1, 4)}) ${targetUser.phone.slice(4, 7)}-${targetUser.phone.slice(7, 9)}-${targetUser.phone.slice(9, 11)}`
                        : '-'}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.birthDate')}
                  </label>
                  {canEdit ? (
                    <>
                      <Input
                        type="date"
                        placeholder={t('profile.birthDate')}
                        {...form.register('birthday')}
                      />
                      {form.formState.errors.birthday && (
                        <p className="text-red-500 text-sm mt-1">
                          {form.formState.errors.birthday.message}
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="flex h-10 w-full rounded-md border border-input bg-gray-50 px-3 py-2 text-sm text-gray-700">
                      {targetUser?.birthday
                        ? new Date(targetUser.birthday).toLocaleDateString()
                        : '-'}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Form Actions - Only show for editable profiles */}
            {canEdit && (
              <div className="flex space-x-4">
                <Button
                  type="submit"
                  disabled={
                    form.formState.isSubmitting || !form.formState.isDirty
                  }
                >
                  {form.formState.isSubmitting
                    ? t('common.saving')
                    : t('common.save')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={!form.formState.isDirty}
                  onClick={() => form.reset()}
                >
                  {t('common.cancel')}
                </Button>
              </div>
            )}
          </form>
        </CardContent>
      </Card>
    </PageLayout>
  );
});

export default Profile;
